import React from 'react';
import { Download, Lock, X } from 'lucide-react';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { DatePicker } from '../../../components/DatePicker';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  SageImportInvoiceReadView,
  type SageImportReadDocument,
} from './SageImportInvoiceReadView';
import { contactDisplayName, type Organization } from '../../../types';
import { CreateOrganizationModal } from '../../contacts/CreateOrganizationModal';
import { isUnassignedIncomingSupplier } from '../utils/invoicePartyDisplay';
import type { InvoiceTab } from '../../../utils/invoiceRoutes';
import { PdfDocumentViewer } from '../../../components/PdfDocumentViewer';
import { PdfPrintButton } from '../../../components/PdfPrintButton';
import { PdfMailShareButton } from '../../../components/PdfMailShareButton';
import {
  documentMailTemplateValues,
  documentTextTypeFromInvoiceDocumentType,
} from '../../leads/utils/documentMailDraft';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import {
  invoiceCustomerColumnLabel,
  invoiceCustomerPartyTarget,
  type InvoicePartyDisplayContext,
} from '../utils/invoicePartyDisplay';
import {
  contactOptionLabel,
  dateInputValue,
  displayOrDash,
  getDisplayId,
  incomingAuftragSelectOptions,
  IncomingInvoiceDuplicateBanner,
  InvoiceEntityLink,
  invoicePartyHref,
  invoiceAuftragPatchFromSelect,
  invoiceAuftragSelectValue,
  outgoingAuftragSelectOptions,
  statusDisplayLabel,
  statusOptionsForRow,
  supplierOrganizations,
  invoiceStatusSelectValue,
  isInvoiceStatusLockedDuringExtraction,
} from '../utils/invoicePreviewUi';
import {
  INCOMING_CREDIT_DOCUMENT_TYPE,
  INCOMING_INVOICE_DOCUMENT_TYPE,
  isIncomingCreditDocumentType,
  isInvoiceImageType,
} from '../utils/incomingInvoice';
import {
  INVOICE_REJECTION_REASON_LABEL,
  NON_INVOICE_KIND_LABEL,
  normalizeNonInvoiceKind,
  normalizeRejectionReason,
  USER_SELECTABLE_REJECTION_REASONS,
} from '../utils/invoiceRejection';
import type { ContactOption, InvoiceDocument, LeadOption, OrganizationOption } from '../types';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { formatIbanDisplay, isValidIban, normalizeIban } from '../../../utils/iban';
import { formatGermanNumber, parseGermanNumberOnBlur } from '../../../utils/parseGermanNumber';
import { todayYMD } from '../../../utils/date';
import { incomingPayableAmount } from '../utils/incomingPayableAmount';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { getCachedMe } from '../../../lib/meClient';
import {
  IncomingInvoiceReviewFindings,
  IncomingInvoiceReviewBar,
  checkToneByField,
  useIncomingInvoiceReview,
} from './IncomingInvoiceReviewPanel';
import { EinvoiceXmlView, isXmlInvoiceFile } from './EinvoiceXmlView';
import { useDatevStatus } from '../../datev/useDatevStatus';
import { useDatevCostCenters } from '../../datev/useDatevCostCenters';

const PREVIEW_INPUT_CLASS =
  'w-full h-9 px-3 border border-app-border rounded-md text-sm text-app-text outline-none focus:border-pipedrive-blue';
const PREVIEW_SELECT_TRIGGER = '!min-h-0 h-9 !py-0 w-full';

/** Beträge kommen als `115.43` aus der API, angezeigt wird deutsches Format. */
function amountInputValue(value: number | string | null | undefined): string {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return formatGermanNumber(n);
}

function PreviewField({
  label,
  children,
  tone,
}: {
  label: string;
  children: React.ReactNode;
  /** Markierung aus der Plausibilitätsprüfung. */
  tone?: 'error' | 'warning';
}) {
  const ring = tone === 'error'
    ? 'rounded-md ring-2 ring-rose-400 ring-offset-2 ring-offset-app-surface'
    : tone === 'warning'
      ? 'rounded-md ring-2 ring-amber-400 ring-offset-2 ring-offset-app-surface'
      : '';
  return (
    <div>
      <p className={`mb-1 text-xs font-medium ${tone === 'error' ? 'text-rose-700' : tone === 'warning' ? 'text-amber-700' : 'text-app-text-muted'}`}>
        {label}
      </p>
      <div className={ring}>{children}</div>
    </div>
  );
}

type DecimalInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className' | 'inputMode'>;

/** Zahlenfeld mit Einheit rechts im Feld. */
function SuffixDecimalInput({ suffix, wrapperClassName = 'flex-1', ...props }: DecimalInputProps & {
  suffix: string;
  wrapperClassName?: string;
}) {
  return (
    <div className={`relative min-w-0 ${wrapperClassName}`}>
      <input {...props} inputMode="decimal" className={`${PREVIEW_INPUT_CLASS} pr-8`} />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-app-text-subtle">
        {suffix}
      </span>
    </div>
  );
}

/** Betragsfeld in Belegwährung (Standard Euro). */
function EuroAmountInput({ currency, ...props }: DecimalInputProps & { currency?: string | null }) {
  const code = String(currency ?? 'EUR').toUpperCase();
  return <SuffixDecimalInput {...props} suffix={code === 'EUR' ? '€' : code} />;
}

const INCOMING_CURRENCIES = ['EUR', 'CHF', 'USD', 'GBP', 'PLN', 'CZK', 'DKK', 'SEK', 'NOK', 'HUF'];

/** Skonto in Euro aus Prozent, kaufmännisch auf Cent gerundet (wie resolvePaymentTerms). */
function skontoAmountFromPercent(base: number, percent: number): number {
  return Math.round(base * percent) / 100;
}

function skontoPercentInputValue(amount: number | string | null | undefined, base: number): string {
  const skonto = Number(amount);
  if (!(skonto > 0) || !(base > 0)) return '';
  return formatGermanNumber((skonto / base) * 100, { minimumFractionDigits: 0 });
}

function impliedRetentionPercent(
  gross: number | string | null | undefined,
  retention: number | string | null | undefined,
): number {
  const face = Number(gross);
  const held = Number(retention);
  if (!(face > 0) || !(held > 0)) return 0;
  return Math.round((held / face) * 10000) / 100;
}

/** Festgeschriebener Wert: als Text, nicht als (deaktiviertes) Eingabefeld. Begründung steht einmal oben. */
function PreviewLockedValue({ children }: { children: React.ReactNode }) {
  return <p className="flex min-h-9 items-center text-sm text-app-text tabular-nums">{children}</p>;
}

function lockedAmountText(value: number | string | null | undefined, currency: string): string {
  const n = Number(value);
  if (value == null || value === '' || !Number.isFinite(n)) return '—';
  return `${formatGermanNumber(n)} ${currency === 'EUR' ? '€' : currency}`;
}

function PreviewReadOnlyValue({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-9 items-center truncate rounded-md border border-app-border bg-app-surface-muted px-3 text-sm text-app-text-muted">
      {children}
    </div>
  );
}

export function InvoiceDocumentPreviewModal({
  row,
  tab,
  leads,
  contacts,
  organizations,
  partyDisplayContext,
  saving,
  onPatch,
  onClose,
  onOpenDuplicate,
  onOrganizationCreated,
  incomingCreditSources = [],
  constructionDeductionsEnabled = false,
  onReviewed,
}: {
  row: InvoiceDocument;
  tab: InvoiceTab;
  leads: LeadOption[];
  contacts: ContactOption[];
  organizations: OrganizationOption[];
  partyDisplayContext: InvoicePartyDisplayContext;
  saving: boolean;
  onPatch: (patch: Record<string, unknown>) => Promise<boolean>;
  onClose: () => void;
  onOpenDuplicate?: (invoiceId: string) => void;
  /** Neu angelegter Lieferant, damit die Auswahllisten des Aufrufers ihn kennen. */
  onOrganizationCreated?: (org: Organization) => void;
  incomingCreditSources?: Array<Pick<InvoiceDocument, 'id' | 'invoice_number' | 'party_name' | 'document_type' | 'organization_id'>>;
  constructionDeductionsEnabled?: boolean;
  /** Nach Freigabe/Bestätigung/Rücknahme: aktualisierte Zeile vom Server. */
  onReviewed?: (row: InvoiceDocument) => void;
}) {
  const isIncoming = tab === 'incoming';
  const datev = useDatevStatus();
  const costCenters = useDatevCostCenters(datev.enabled && row.document_type !== 'offer');
  const costCenterOptions = React.useMemo(
    () => [
      { value: '', label: 'Keine' },
      ...costCenters
        .filter((center) => center.active || center.id === row.cost_center_id)
        .map((center) => ({ value: center.id, label: center.name ? `${center.code} · ${center.name}` : center.code })),
    ],
    [costCenters, row.cost_center_id],
  );
  const fileUrl = `/api/invoices/${encodeURIComponent(row.id)}/file`;
  const xmlUrl = `${fileUrl}?part=xml`;
  const isPdfFile =
    String(row.mime_type ?? '').toLowerCase().includes('pdf') || /\.pdf$/i.test(row.filename ?? '');
  // Fotografierte Belege: als Bild zeigen; HEIC liefert der Server dafür als JPEG.
  const isImageFile = !isPdfFile && isInvoiceImageType({ filename: row.filename ?? '', mimeType: row.mime_type ?? '' });
  const numberLocked = !isIncoming && !['draft', 'extraction_pending', 'extraction_failed'].includes(row.status);
  // GoBD: freigegebene Eingangsrechnungen und gebuchte Ausgangsrechnungen sind festgeschrieben.
  const incomingLocked = isIncoming && Boolean(row.approved_at);
  // Ausgestellte Ausgangsrechnungen (nicht Angebote) sind festgeschrieben.
  const outgoingLocked = tab === 'outgoing' && numberLocked;
  const coreLocked = incomingLocked || outgoingLocked;
  const fieldTone = isIncoming ? checkToneByField(row) : {};
  const { can } = useCapabilities();
  const currentUserId = getCachedMe()?.user?.id ?? null;
  const canApprove = can('web.rechnungen.approve');
  // ZUGFeRD/XRechnung als PDF: lesbares PDF plus maßgebliches XML, umschaltbar.
  const xmlOnly = isXmlInvoiceFile(row);
  const hasEinvoiceXml =
    !xmlOnly
    && (Boolean(row.original_xml_object_path)
      || row.extraction_method === 'zugferd'
      || row.extraction_method === 'xrechnung'
      || outgoingLocked);
  const [viewMode, setViewMode] = React.useState<'pdf' | 'xml'>('pdf');
  React.useEffect(() => setViewMode('pdf'), [row.id]);
  const review = useIncomingInvoiceReview({ row, currentUserId, onReviewed: (next) => onReviewed?.(next) });
  const showReviewBar = isIncoming && review.applies;
  const currency = String(row.currency || 'EUR').toUpperCase();
  const [rateDraft, setRateDraft] = React.useState(() =>
    row.exchange_rate != null && row.exchange_rate !== '' ? formatGermanNumber(Number(row.exchange_rate), { minimumFractionDigits: 0, maximumFractionDigits: 6 }) : '',
  );
  React.useEffect(() => {
    setRateDraft(
      row.exchange_rate != null && row.exchange_rate !== ''
        ? formatGermanNumber(Number(row.exchange_rate), { minimumFractionDigits: 0, maximumFractionDigits: 6 })
        : '',
    );
  }, [row.id, row.exchange_rate]);
  const [numberDraft, setNumberDraft] = React.useState(row.invoice_number ?? '');
  const [grossDraft, setGrossDraft] = React.useState(() => amountInputValue(row.gross_amount));
  const [netDraft, setNetDraft] = React.useState(() => amountInputValue(row.net_amount));
  const [taxDraft, setTaxDraft] = React.useState(() => amountInputValue(row.tax_amount));
  const [payeeIbanDraft, setPayeeIbanDraft] = React.useState(formatIbanDisplay(row.payee_iban));
  const [payeeBicDraft, setPayeeBicDraft] = React.useState(row.payee_bic ?? '');
  const [payeeNameDraft, setPayeeNameDraft] = React.useState(row.payee_name ?? '');
  const [paymentReferenceDraft, setPaymentReferenceDraft] = React.useState(row.payment_reference ?? '');
  const [payeeIbanError, setPayeeIbanError] = React.useState<string | null>(null);
  const [skontoTaken, setSkontoTaken] = React.useState(Boolean(row.skonto_taken));
  const [skontoDraft, setSkontoDraft] = React.useState(() => amountInputValue(row.skonto_amount));
  const [retentionPercentDraft, setRetentionPercentDraft] = React.useState(() => {
    const percent = impliedRetentionPercent(row.gross_amount, row.retention_gross);
    return percent > 0 ? formatGermanNumber(percent) : '';
  });
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const [createSupplierOpen, setCreateSupplierOpen] = React.useState(false);
  const unassignedSupplierName = isIncoming && isUnassignedIncomingSupplier(row) ? String(row.party_name ?? '').trim() : '';
  useEscapeClose(onClose, !saving && !createSupplierOpen);
  const rejectionReason = row.status === 'rejected' ? normalizeRejectionReason(row.rejection_reason) : null;
  const nonInvoiceKind = normalizeNonInvoiceKind(row.non_invoice_kind);
  const showRejectionReason = isIncoming && row.status === 'rejected' && rejectionReason !== 'reclassified';

  React.useEffect(() => {
    dialogRef.current?.focus();
  }, [row.id]);

  React.useEffect(() => {
    setNumberDraft(row.invoice_number ?? '');
    setGrossDraft(amountInputValue(row.gross_amount));
    setNetDraft(amountInputValue(row.net_amount));
    setTaxDraft(amountInputValue(row.tax_amount));
    setPayeeIbanDraft(formatIbanDisplay(row.payee_iban));
    setPayeeBicDraft(row.payee_bic ?? '');
    setPayeeNameDraft(row.payee_name ?? '');
    setPaymentReferenceDraft(row.payment_reference ?? '');
    setPayeeIbanError(null);
    setSkontoTaken(Boolean(row.skonto_taken));
    setSkontoDraft(amountInputValue(row.skonto_amount));
    const percent = impliedRetentionPercent(row.gross_amount, row.retention_gross);
    setRetentionPercentDraft(percent > 0 ? formatGermanNumber(percent) : '');
  }, [row.id, row.invoice_number, row.gross_amount, row.net_amount, row.tax_amount, row.payee_iban, row.payee_bic, row.payee_name, row.payment_reference, row.skonto_taken, row.skonto_amount, row.retention_gross]);

  const customerLabel = invoiceCustomerColumnLabel(row, isIncoming, partyDisplayContext);
  const customerTarget = invoiceCustomerPartyTarget(row, isIncoming, partyDisplayContext);
  const customerHref = invoicePartyHref(customerTarget);
  const matchedContact = contacts.find((contact) => {
    const name = (contactDisplayName(contact) || contact.company || contact.name || '').trim();
    return name && name === (row.party_name || '').trim();
  }) ?? (customerTarget?.kind === 'contact'
    ? contacts.find((contact) => String(contact.id) === customerTarget.id)
    : undefined);
  const supplierOrgs = supplierOrganizations(organizations, row.organization_id);
  const dropdownZ = UI_Z_INDEX.portalDropdownInModal;
  const isIncomingCredit = isIncomingCreditDocumentType(row.document_type);
  const hideDueDate =
    row.document_type === 'credit_note'
    || row.document_type === 'invoice_correction'
    || isIncomingCredit;
  const paidLabel =
    tab === 'quotes'
      ? 'Angenommen am'
      : row.document_type === 'invoice_correction'
        ? 'Ausbezahlt am'
        : isIncomingCredit
          ? 'Verrechnet am'
          : 'Bezahlt am';
  const creditedInvoiceOptions = incomingCreditSources.filter((item) => {
    if (item.id === row.id || isIncomingCreditDocumentType(item.document_type)) return false;
    const orgId = String(row.organization_id ?? '').trim();
    if (!orgId) return true;
    return String(item.organization_id ?? '').trim() === orgId;
  });

  const canEditRetention =
    constructionDeductionsEnabled
    && !isIncoming
    && ['invoice', 'partial_invoice', 'final_invoice'].includes(String(row.document_type))
    && row.status !== 'cancelled'
    && row.retention_status !== 'released_on_final';

  const retentionPreviewGross = React.useMemo(() => {
    const face = Number(row.gross_amount);
    const parsed = retentionPercentDraft.trim() ? parseGermanNumberOnBlur(retentionPercentDraft.trim()) : 0;
    if (!(face > 0) || parsed == null || parsed <= 0) return 0;
    return Math.round(Math.min(face, (face * Math.min(100, parsed)) / 100) * 100) / 100;
  }, [retentionPercentDraft, row.gross_amount]);

  const payableBase = React.useMemo(() => {
    if (row.payable_amount != null && String(row.payable_amount).trim() !== '') {
      const n = Number(row.payable_amount);
      if (Number.isFinite(n)) return n;
    }
    const gross = Number(row.gross_amount);
    return Number.isFinite(gross) ? gross : 0;
  }, [row.payable_amount, row.gross_amount]);

  // Skonto ist nur als Betrag gespeichert (KI setzt ihn direkt); Prozent ist die zweite Eingabeform.
  const [skontoPercentDraft, setSkontoPercentDraft] = React.useState(() =>
    skontoPercentInputValue(row.skonto_amount, payableBase),
  );
  React.useEffect(() => {
    // Getippte Prozente („2“) stehen lassen, solange sie zum gespeicherten Betrag führen,
    // statt sie durch den zurückgerechneten Wert („2,01“) zu ersetzen.
    setSkontoPercentDraft((draft) => {
      const typed = draft.trim() ? parseGermanNumberOnBlur(draft.trim()) : null;
      const amount = Number(row.skonto_amount);
      if (typed != null && amount > 0 && skontoAmountFromPercent(payableBase, typed) === amount) return draft;
      return skontoPercentInputValue(row.skonto_amount, payableBase);
    });
  }, [row.id, row.skonto_amount, payableBase]);

  // Eingang: Rechnungsbetrag bleibt wie auf dem Beleg, Zahlbetrag ist abgeleitet – dieselbe Regel
  // wie im SEPA-Export, bezogen auf heute.
  const incomingPayment = isIncoming && !isIncomingCredit
    ? incomingPayableAmount({
        baseAmount: payableBase,
        skontoAmount: row.skonto_amount,
        skontoDueDate: row.skonto_due_date,
        skontoTaken,
        paymentDate: todayYMD(),
      })
    : null;
  const incomingSkontoAmount = Number(row.skonto_amount) || 0;

  const commitSkontoPercent = () => {
    const trimmed = skontoPercentDraft.trim();
    if (!trimmed) {
      setSkontoDraft('');
      void commitAmountIfChanged(row.skonto_amount, '', 'skonto_amount', setSkontoDraft);
      return;
    }
    const percent = parseGermanNumberOnBlur(trimmed);
    if (percent == null || percent < 0 || percent > 100 || !(payableBase > 0)) {
      setSkontoPercentDraft(skontoPercentInputValue(row.skonto_amount, payableBase));
      return;
    }
    setSkontoPercentDraft(formatGermanNumber(percent, { minimumFractionDigits: 0 }));
    const amountText = amountInputValue(skontoAmountFromPercent(payableBase, percent));
    setSkontoDraft(amountText);
    void commitAmountIfChanged(row.skonto_amount, amountText, 'skonto_amount', setSkontoDraft);
  };

  const commitIfChanged = async (current: string, next: string, patch: Record<string, unknown>) => {
    if (next.trim() === current.trim()) return;
    await onPatch(patch);
  };

  const commitAmountIfChanged = async (
    current: number | string | null | undefined,
    draft: string,
    field: 'net_amount' | 'tax_amount' | 'gross_amount' | 'skonto_amount',
    setDraft: (value: string) => void,
  ) => {
    const trimmed = draft.trim();
    const parsed = trimmed ? parseGermanNumberOnBlur(trimmed) : null;
    if (trimmed && parsed == null) {
      setDraft(amountInputValue(current));
      return;
    }
    const currentNumber = current == null || current === '' ? null : Number(current);
    setDraft(amountInputValue(parsed));
    if (parsed === currentNumber) return;
    const patch: Record<string, unknown> = { [field]: parsed };
    if (field === 'skonto_amount') {
      const nextSkonto = parsed != null && parsed > 0 ? parsed : 0;
      if (nextSkonto <= 0) {
        setSkontoTaken(false);
        patch.skonto_taken = false;
      }
      if (row.paid_at) {
        patch.paid_amount = Math.max(0, payableBase - (skontoTaken && nextSkonto > 0 ? nextSkonto : 0));
      }
    }
    await onPatch(patch);
  };

  const [sageRead, setSageRead] = React.useState<SageImportReadDocument | null>(null);
  React.useEffect(() => {
    if (row.source !== 'sage_import') {
      setSageRead(null);
      return;
    }
    let cancelled = false;
    setSageRead(null);
    fetch(`/api/invoices/${encodeURIComponent(row.id)}`, { credentials: 'include' })
      .then(async (response) => (response.ok ? response.json() : null))
      .then((payload: { sage_read?: SageImportReadDocument } | null) => {
        if (!cancelled) setSageRead(payload?.sage_read ?? null);
      })
      .catch(() => {
        if (!cancelled) setSageRead(null);
      });
    return () => {
      cancelled = true;
    };
  }, [row.id, row.source]);

  // Sage-Belege mit erzeugtem PDF nutzen die normale Vorschau; ohne Datei bleibt die Leseansicht.
  if (row.source === 'sage_import' && !row.lead_document_id) {
    return (
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="fixed inset-0 z-[260] flex items-stretch justify-center bg-slate-900/55 p-0 outline-none md:items-center md:p-4"
        role="dialog"
        aria-modal="true"
        aria-label={row.invoice_number || 'Beleg'}
      >
        <div className="flex h-full w-full flex-col bg-app-surface md:h-auto md:max-h-[92vh] md:max-w-3xl md:rounded-md md:border md:border-app-border">
          <div className="flex items-center justify-between border-b border-app-border px-4 py-3">
            <h2 className="text-sm font-semibold">{row.invoice_number || 'Beleg'}</h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 items-center rounded-md px-3 text-sm text-app-text-muted hover:bg-app-hover"
            >
              Schließen
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {sageRead ? <SageImportInvoiceReadView document={sageRead} /> : <InlineLoadingState label="Beleg wird geladen …" />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className="fixed inset-0 z-[260] flex items-stretch md:items-center justify-center bg-slate-900/55 p-0 md:p-4 outline-none"
      role="dialog"
      aria-modal="true"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="w-full md:max-w-6xl h-full md:h-[86vh] rounded-none md:rounded-xl bg-app-surface shadow-2xl border-0 md:border border-app-border overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-app-border bg-app-surface-muted">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-app-text truncate">{getDisplayId(row)}</h2>
            <p className="text-xs text-app-text-subtle truncate">{row.filename}</p>
          </div>
          <div className="flex items-center gap-2">
            {saving ? <LoadingSpinner size="sm" /> : null}
            {hasEinvoiceXml ? (
              <div className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface p-0.5 text-xs font-medium" role="tablist" aria-label="Ansicht">
                {(['pdf', 'xml'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="tab"
                    aria-selected={viewMode === mode}
                    onClick={() => setViewMode(mode)}
                    className={`h-full rounded px-2.5 ${viewMode === mode ? 'bg-app-surface-muted text-app-text' : 'text-app-text-muted hover:text-app-text'}`}
                    title={mode === 'xml' ? 'Maßgeblich sind die Daten der E-Rechnung (XML).' : undefined}
                  >
                    {mode === 'pdf' ? 'PDF' : 'E-Rechnung'}
                  </button>
                ))}
              </div>
            ) : null}
            {isPdfFile ? (
              <>
                <PdfMailShareButton
                  url={fileUrl}
                  filename={row.filename || `${row.invoice_number || 'beleg'}.pdf`}
                  leadId={row.lead_id}
                  documentTextType={documentTextTypeFromInvoiceDocumentType(row.document_type)}
                  templateValues={documentMailTemplateValues(row)}
                  prefillRecipient={!isIncoming}
                  onBeforeOpen={onClose}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-app-border bg-app-surface px-3.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                />
                <PdfPrintButton
                  url={fileUrl}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-app-border bg-app-surface px-3.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                />
              </>
            ) : null}
            <a
              href={`${fileUrl}?download=1`}
              download={row.filename || true}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-app-border bg-app-surface px-3.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
            >
              <Download className="w-4 h-4" />
              Download
            </a>
            <button type="button" onClick={onClose} className="h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-app-text-muted">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
          {xmlOnly ? (
            <EinvoiceXmlView url={fileUrl} className="min-h-[40vh] w-full md:min-h-0" />
          ) : hasEinvoiceXml && viewMode === 'xml' ? (
            <EinvoiceXmlView url={xmlUrl} className="min-h-[40vh] w-full md:min-h-0" />
          ) : isImageFile ? (
            <div className="app-scrollbar flex min-h-[40vh] w-full min-w-0 flex-1 items-start justify-center overflow-auto bg-app-surface-muted p-4 md:min-h-0">
              <img src={fileUrl} alt={row.filename || 'Beleg'} className="max-w-full rounded shadow-sm" />
            </div>
          ) : (
            <PdfDocumentViewer
              url={fileUrl}
              title={row.filename || 'Dokument'}
              className="min-h-[40vh] w-full md:min-h-0"
            />
          )}
          <aside className="app-scrollbar h-[48vh] w-full shrink-0 overflow-y-auto overscroll-contain border-t border-app-border p-4 [scrollbar-gutter:stable] md:h-auto md:min-h-0 md:w-[340px] md:border-t-0 md:border-l">
            {unassignedSupplierName || showReviewBar ? (
              // Kopfleiste der Seitenleiste: Prüfung/Freigabe, darunter offene Stammdaten. Bleibt beim Scrollen sichtbar.
              <div className="sticky -top-4 z-10 -mx-4 -mt-4 mb-3 border-b border-app-border bg-app-surface">
            {showReviewBar ? (
              <IncomingInvoiceReviewBar
                review={review}
                canApprove={canApprove}
                saving={saving}
                onReject={() => { void onPatch({ status: 'rejected' }); }}
              />
            ) : null}
            {unassignedSupplierName ? (
              <div className="flex items-center gap-2 border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-950 dark:text-amber-300">
                <span className="min-w-0 flex-1 truncate" title={`Lieferant „${unassignedSupplierName}“ ist nicht als Organisation angelegt.`}>
                  Lieferant nicht angelegt
                </span>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setCreateSupplierOpen(true)}
                  className="shrink-0 font-medium underline disabled:opacity-60"
                >
                  Anlegen
                </button>
              </div>
            ) : null}
            {isIncoming ? <IncomingInvoiceReviewFindings review={review} canApprove={canApprove} /> : null}
              </div>
            ) : null}
            <div className="space-y-3">
              {outgoingLocked ? (
                <p className="flex items-start gap-2 text-xs text-app-text-muted">
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>Ausgestellt und festgeschrieben. Korrekturen über Storno oder Gutschrift; Zahlung, Skonto und Status bleiben änderbar.</span>
                </p>
              ) : null}
              {row.reclassified_to_id ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Dieser Beleg wurde {isIncoming ? 'in eine Ausgangsrechnung' : 'in eine Eingangsrechnung'} umgewandelt
                  und bleibt nur zur Nachvollziehbarkeit erhalten.
                  {onOpenDuplicate ? (
                    <button
                      type="button"
                      onClick={() => onOpenDuplicate(String(row.reclassified_to_id))}
                      className="ml-1 font-medium underline"
                    >
                      Neuen Beleg öffnen
                    </button>
                  ) : null}
                </div>
              ) : null}
              {row.reclassified_from_id ? (
                <div className="rounded-md border border-app-border bg-app-surface-muted px-3 py-2 text-xs text-app-text-muted">
                  Umgewandelt aus {isIncoming ? 'einer Ausgangsrechnung' : 'einer Eingangsrechnung'}.
                  {onOpenDuplicate ? (
                    <button
                      type="button"
                      onClick={() => onOpenDuplicate(String(row.reclassified_from_id))}
                      className="ml-1 font-medium underline"
                    >
                      Ursprung öffnen
                    </button>
                  ) : null}
                </div>
              ) : null}
              {showRejectionReason && rejectionReason === 'not_invoice' ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <p>
                    {nonInvoiceKind
                      ? `Kein Rechnungsbeleg erkannt (${NON_INVOICE_KIND_LABEL[nonInvoiceKind]}).`
                      : 'Als „Keine Rechnung“ markiert.'}{' '}
                    Der Beleg bleibt archiviert, wird aber nicht gebucht.
                  </p>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => { void onPatch({ status: 'draft' }); }}
                    className="mt-1 font-medium underline disabled:opacity-60"
                  >
                    Doch als Rechnung übernehmen
                  </button>
                </div>
              ) : null}
              {isIncoming && row.duplicate_of ? (
                <IncomingInvoiceDuplicateBanner duplicateOf={row.duplicate_of} onOpenExisting={onOpenDuplicate} />
              ) : null}
              {isIncoming ? (
                <PreviewField label="Belegart">
                  {incomingLocked ? (
                    <PreviewLockedValue>{isIncomingCredit ? 'Gutschrift' : 'Rechnung'}</PreviewLockedValue>
                  ) : (
                  <AppSelectDropdown
                    value={isIncomingCredit ? INCOMING_CREDIT_DOCUMENT_TYPE : INCOMING_INVOICE_DOCUMENT_TYPE}
                    onChange={(value) => { void onPatch({ document_type: value }); }}
                    options={[
                      { value: INCOMING_INVOICE_DOCUMENT_TYPE, label: 'Rechnung' },
                      { value: INCOMING_CREDIT_DOCUMENT_TYPE, label: 'Gutschrift' },
                    ]}
                    disabled={saving}
                    triggerClassName={PREVIEW_SELECT_TRIGGER}
                    menuZIndex={dropdownZ}
                  />
                  )}
                </PreviewField>
              ) : null}
              <PreviewField label="Nummer" tone={fieldTone.invoice_number}>
                {coreLocked || numberLocked ? (
                  <PreviewLockedValue>{displayOrDash(row.invoice_number || row.reference_id)}</PreviewLockedValue>
                ) : (
                <input
                  value={numberDraft}
                  disabled={saving || coreLocked}
                  onChange={(e) => setNumberDraft(e.target.value)}
                  onBlur={() => {
                    void commitIfChanged(row.invoice_number ?? '', numberDraft, { invoice_number: numberDraft.trim() || null });
                  }}
                  className={`${PREVIEW_INPUT_CLASS} disabled:bg-app-surface-muted disabled:text-app-text-subtle`}
                />
                )}
              </PreviewField>
              <PreviewField label="Auftrag" tone={fieldTone.lead_id}>
                <AppSelectDropdown
                  value={invoiceAuftragSelectValue(row)}
                  onChange={(value) => { void onPatch(invoiceAuftragPatchFromSelect(value)); }}
                  options={isIncoming ? incomingAuftragSelectOptions(leads) : outgoingAuftragSelectOptions(leads)}
                  placeholder="Projekt auswählen"
                  searchable
                  searchPlaceholder="Projekte suchen…"
                  disabled={saving}
                  triggerClassName={PREVIEW_SELECT_TRIGGER}
                  menuZIndex={dropdownZ}
                />
              </PreviewField>
              {costCenters.length > 0 ? (
                <PreviewField label="Kostenstelle">
                  {row.datev_status === 'exported' ? (
                    <PreviewReadOnlyValue>
                      {costCenterOptions.find((option) => option.value === (row.cost_center_id ?? ''))?.label ?? 'Keine'}
                    </PreviewReadOnlyValue>
                  ) : (
                    <AppSelectDropdown
                      value={row.cost_center_id ?? ''}
                      onChange={(value) => { void onPatch({ cost_center_id: value || null }); }}
                      options={costCenterOptions}
                      disabled={saving}
                      triggerClassName={PREVIEW_SELECT_TRIGGER}
                      menuZIndex={dropdownZ}
                    />
                  )}
                </PreviewField>
              ) : null}
              <PreviewField label="Kunde">
                {isIncoming ? (
                  <PreviewReadOnlyValue>
                    {customerLabel && customerHref ? (
                      <InvoiceEntityLink to={customerHref} title="Kunde öffnen">
                        {customerLabel}
                      </InvoiceEntityLink>
                    ) : (
                      displayOrDash(customerLabel)
                    )}
                  </PreviewReadOnlyValue>
                ) : outgoingLocked ? (
                  <PreviewLockedValue>
                    {customerLabel && customerHref ? (
                      <InvoiceEntityLink to={customerHref} title="Kunde öffnen">
                        {customerLabel}
                      </InvoiceEntityLink>
                    ) : (
                      displayOrDash(row.party_name)
                    )}
                  </PreviewLockedValue>
                ) : (
                  <AppSelectDropdown
                    value={matchedContact ? String(matchedContact.id) : ''}
                    onChange={(value) => {
                      const contact = contacts.find((item) => String(item.id) === value);
                      if (!contact) return;
                      void onPatch({
                        party_name: contactDisplayName(contact) || contact.company || contact.name || '',
                      });
                    }}
                    options={contacts.map((contact) => ({
                      value: String(contact.id),
                      label: contactOptionLabel(contact),
                    }))}
                    placeholder={row.party_name?.trim() || 'Kontakt auswählen'}
                    searchable
                    searchPlaceholder="Kontakte suchen…"
                    disabled={saving}
                    triggerClassName={PREVIEW_SELECT_TRIGGER}
                    menuZIndex={dropdownZ}
                  />
                )}
              </PreviewField>
              {isIncoming ? (
                <PreviewField label="Lieferant" tone={fieldTone.organization_id}>
                  {incomingLocked && row.organization_id ? (
                    <PreviewLockedValue>{displayOrDash(supplierOrgs.find((org) => org.id === row.organization_id)?.name ?? row.party_name)}</PreviewLockedValue>
                  ) : (
                  <AppSelectDropdown
                    value={row.organization_id ?? ''}
                    onChange={(value) => {
                      const org = supplierOrgs.find((item) => item.id === value);
                      void onPatch({
                        organization_id: value || null,
                        party_name: org?.name ?? null,
                      });
                    }}
                    options={[
                      {
                        value: '',
                        label: unassignedSupplierName ? `${unassignedSupplierName} (nicht angelegt)` : 'Kein Lieferant',
                      },
                      ...supplierOrgs.map((org) => ({ value: org.id, label: org.name })),
                    ]}
                    placeholder="Lieferant auswählen"
                    searchable
                    searchPlaceholder="Lieferanten suchen…"
                    disabled={saving}
                    triggerClassName={PREVIEW_SELECT_TRIGGER}
                    menuZIndex={dropdownZ}
                  />
                  )}
                </PreviewField>
              ) : null}
              {isIncomingCredit ? (
                <PreviewField label="Zu Rechnung">
                  <AppSelectDropdown
                    value={row.credited_invoice_id ?? ''}
                    onChange={(value) => { void onPatch({ credited_invoice_id: value || null }); }}
                    options={[
                      { value: '', label: 'Keine Verknüpfung' },
                      ...creditedInvoiceOptions.map((item) => ({
                        value: item.id,
                        label: [item.invoice_number, item.party_name].filter(Boolean).join(' · ') || item.id.slice(0, 8),
                      })),
                    ]}
                    placeholder="Eingangsrechnung wählen"
                    searchable
                    searchPlaceholder="Rechnungen suchen…"
                    disabled={saving}
                    triggerClassName={PREVIEW_SELECT_TRIGGER}
                    menuZIndex={dropdownZ}
                  />
                </PreviewField>
              ) : null}
              <PreviewField label="Rechnungsdatum" tone={fieldTone.issue_date}>
                {coreLocked && row.issue_date ? (
                  <PreviewLockedValue>{dateInputValue(row.issue_date).split('-').reverse().join('.')}</PreviewLockedValue>
                ) : (
                  <DatePicker
                    value={dateInputValue(row.issue_date)}
                    onChange={(value) => { void onPatch({ issue_date: value || null }); }}
                    className="w-full"
                    menuZIndex={dropdownZ}
                  />
                )}
              </PreviewField>
              {hideDueDate ? null : (
                <PreviewField label="Fälligkeit" tone={fieldTone.due_date}>
                  <DatePicker
                    value={dateInputValue(row.due_date)}
                    onChange={(value) => { void onPatch({ due_date: value || null }); }}
                    className="w-full"
                    menuZIndex={dropdownZ}
                  />
                </PreviewField>
              )}
              <PreviewField label={paidLabel}>
                <DatePicker
                  value={dateInputValue(row.paid_at)}
                  onChange={(value) => {
                    const patch: Record<string, unknown> = { paid_at: value || null };
                    if (value) {
                      const payable =
                        row.payable_amount != null && String(row.payable_amount).trim() !== ''
                          ? Number(row.payable_amount)
                          : Number(row.gross_amount);
                      const skonto = Number(row.skonto_amount) || 0;
                      if (skontoTaken) {
                        patch.skonto_taken = true;
                        patch.paid_amount = Math.max(0, (Number.isFinite(payable) ? payable : 0) - skonto);
                      } else if (Number.isFinite(payable)) {
                        patch.paid_amount = payable;
                      }
                    } else {
                      patch.skonto_taken = false;
                      patch.retention_held = false;
                      patch.paid_amount = null;
                    }
                    void onPatch(patch);
                  }}
                  className="w-full"
                  menuZIndex={dropdownZ}
                />
              </PreviewField>
              {!isIncomingCredit ? (
              <PreviewField label="Skonto">
                <div className="flex items-center gap-2">
                  <SuffixDecimalInput
                    suffix="%"
                    wrapperClassName="w-[5.5rem] shrink-0"
                    value={skontoPercentDraft}
                    disabled={saving || !(payableBase > 0)}
                    placeholder="0"
                    onChange={(e) => setSkontoPercentDraft(e.target.value)}
                    onBlur={commitSkontoPercent}
                    title={payableBase > 0 ? `Prozent von ${formatGermanNumber(payableBase)} €` : 'Erst den Betrag eintragen'}
                    aria-label="Skonto in Prozent"
                  />
                  <EuroAmountInput
                    value={skontoDraft}
                    disabled={saving}
                    placeholder="0,00"
                    onChange={(e) => setSkontoDraft(e.target.value)}
                    onBlur={() => {
                      void commitAmountIfChanged(row.skonto_amount, skontoDraft, 'skonto_amount', setSkontoDraft);
                    }}
                    aria-label="Skontobetrag in Euro"
                  />
                  <label className="inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap text-sm text-app-text">
                    <input
                      type="checkbox"
                      checked={skontoTaken}
                      disabled={saving || (Number(row.skonto_amount) || 0) <= 0}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setSkontoTaken(next);
                        const skonto = Number(row.skonto_amount) || 0;
                        const patch: Record<string, unknown> = { skonto_taken: next };
                        if (row.paid_at) {
                          patch.paid_amount = Math.max(0, payableBase - (next ? skonto : 0));
                        }
                        void onPatch(patch);
                      }}
                    />
                    gezogen
                  </label>
                </div>
                {payableBase > 0 && (skontoPercentDraft.trim() || Number(row.skonto_amount) > 0) ? (
                  <p className="mt-1 text-xs text-app-text-subtle tabular-nums">
                    Prozent von {formatGermanNumber(payableBase)} €
                  </p>
                ) : null}
              </PreviewField>
              ) : null}
              {isIncoming && !isIncomingCredit && Number(row.skonto_amount) > 0 ? (
                // Entscheidet im SEPA-Export, ob Skonto abgezogen wird; ohne Frist nur bei „gezogen“.
                <PreviewField label="Skontofrist">
                  <DatePicker
                    value={dateInputValue(row.skonto_due_date)}
                    onChange={(value) => { void onPatch({ skonto_due_date: value || null }); }}
                    className="w-full"
                    menuZIndex={dropdownZ}
                  />
                </PreviewField>
              ) : null}
              {canEditRetention ? (
                <PreviewField label="Einbehalt %">
                  <div className="flex items-center gap-2">
                    <input
                      inputMode="decimal"
                      value={retentionPercentDraft}
                      disabled={saving}
                      placeholder="0"
                      onChange={(e) => setRetentionPercentDraft(e.target.value)}
                      onBlur={() => {
                        const trimmed = retentionPercentDraft.trim();
                        const parsed = trimmed ? parseGermanNumberOnBlur(trimmed) : 0;
                        const current = impliedRetentionPercent(row.gross_amount, row.retention_gross);
                        if (trimmed && (parsed == null || parsed < 0 || parsed > 100)) {
                          setRetentionPercentDraft(current > 0 ? formatGermanNumber(current) : '');
                          return;
                        }
                        const next = parsed ?? 0;
                        if (Math.abs(next - current) < 0.001) {
                          setRetentionPercentDraft(current > 0 ? formatGermanNumber(current) : '');
                          return;
                        }
                        void onPatch({ retention_percent: next }).then((ok) => {
                          if (!ok) setRetentionPercentDraft(current > 0 ? formatGermanNumber(current) : '');
                        });
                      }}
                      className={PREVIEW_INPUT_CLASS}
                      aria-label="Einbehalt in Prozent vom Brutto"
                    />
                    <span className="shrink-0 text-sm tabular-nums text-app-text-muted">
                      {retentionPreviewGross.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      €
                    </span>
                  </div>
                </PreviewField>
              ) : null}
              {!isIncoming && row.payable_amount != null &&
              (Number(row.payable_amount) > 0 || Number(row.retention_gross) > 0) ? (
                <PreviewField label="Zahlbetrag">
                  <PreviewReadOnlyValue>
                    {Number(row.payable_amount).toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    €
                  </PreviewReadOnlyValue>
                </PreviewField>
              ) : null}
              <PreviewField label="Netto" tone={fieldTone.net_amount}>
                {coreLocked && row.net_amount != null && row.net_amount !== '' ? (
                  <PreviewLockedValue>{lockedAmountText(row.net_amount, isIncoming ? currency : 'EUR')}</PreviewLockedValue>
                ) : (
                <EuroAmountInput
                  currency={isIncoming ? currency : 'EUR'}
                  value={netDraft}
                  disabled={saving}
                  onChange={(e) => setNetDraft(e.target.value)}
                  onBlur={() => {
                    void commitAmountIfChanged(row.net_amount, netDraft, 'net_amount', setNetDraft);
                  }}
                  aria-label="Netto in Euro"
                />
                )}
              </PreviewField>
              <PreviewField label="Steuer" tone={fieldTone.tax_amount}>
                {coreLocked && row.tax_amount != null && row.tax_amount !== '' ? (
                  <PreviewLockedValue>{lockedAmountText(row.tax_amount, isIncoming ? currency : 'EUR')}</PreviewLockedValue>
                ) : (
                <EuroAmountInput
                  currency={isIncoming ? currency : 'EUR'}
                  value={taxDraft}
                  disabled={saving}
                  onChange={(e) => setTaxDraft(e.target.value)}
                  onBlur={() => {
                    void commitAmountIfChanged(row.tax_amount, taxDraft, 'tax_amount', setTaxDraft);
                  }}
                  aria-label="Steuer in Euro"
                />
                )}
              </PreviewField>
              <PreviewField label="Betrag" tone={fieldTone.gross_amount}>
                {coreLocked && row.gross_amount != null && row.gross_amount !== '' ? (
                  <PreviewLockedValue>{lockedAmountText(row.gross_amount, isIncoming ? currency : 'EUR')}</PreviewLockedValue>
                ) : (
                <EuroAmountInput
                  currency={isIncoming ? currency : 'EUR'}
                  value={grossDraft}
                  disabled={saving}
                  onChange={(e) => setGrossDraft(e.target.value)}
                  onBlur={() => {
                    void commitAmountIfChanged(row.gross_amount, grossDraft, 'gross_amount', setGrossDraft);
                  }}
                  aria-label="Betrag in Euro"
                />
                )}
              </PreviewField>
              {isIncoming ? (
                <PreviewField label="Währung">
                  {incomingLocked ? (
                    <PreviewLockedValue>{currency}</PreviewLockedValue>
                  ) : (
                  <AppSelectDropdown
                    value={currency}
                    onChange={(value) => { void onPatch({ currency: value }); }}
                    options={Array.from(new Set([currency, ...INCOMING_CURRENCIES])).map((code) => ({ value: code, label: code }))}
                    disabled={saving}
                    triggerClassName={PREVIEW_SELECT_TRIGGER}
                    menuZIndex={dropdownZ}
                  />
                  )}
                </PreviewField>
              ) : null}
              {isIncoming && currency !== 'EUR' ? (
                <PreviewField label={`Kurs (${currency} je 1 €)`} tone={fieldTone.exchange_rate}>
                  <input
                    inputMode="decimal"
                    value={rateDraft}
                    disabled={saving || incomingLocked}
                    placeholder="EZB-Kurs fehlt"
                    onChange={(e) => setRateDraft(e.target.value)}
                    onBlur={() => {
                      const trimmed = rateDraft.trim();
                      const parsed = trimmed ? parseGermanNumberOnBlur(trimmed) : null;
                      const current = row.exchange_rate == null || row.exchange_rate === '' ? null : Number(row.exchange_rate);
                      if ((trimmed && (parsed == null || parsed <= 0)) || parsed === current) {
                        setRateDraft(current != null ? formatGermanNumber(current, { minimumFractionDigits: 0, maximumFractionDigits: 6 }) : '');
                        return;
                      }
                      void onPatch({ exchange_rate: parsed });
                    }}
                    className={PREVIEW_INPUT_CLASS}
                    aria-label="Umrechnungskurs"
                  />
                  <p className="mt-1 text-xs text-app-text-subtle">
                    {row.exchange_rate_source === 'ecb'
                      ? `EZB-Referenzkurs vom ${dateInputValue(row.exchange_rate_date ?? null).split('-').reverse().join('.')}`
                      : row.exchange_rate_source === 'manual'
                        ? 'Manuell eingetragen'
                        : 'Kein Kurs hinterlegt'}
                    {row.gross_amount_eur != null && row.gross_amount_eur !== ''
                      ? ` · ${formatGermanNumber(Number(row.gross_amount_eur))} € brutto`
                      : ''}
                  </p>
                </PreviewField>
              ) : null}
              {incomingPayment?.hasSkonto ? (
                <PreviewField label="Zahlbetrag">
                  <PreviewReadOnlyValue>
                    <span className="tabular-nums">{formatGermanNumber(incomingPayment.amount)} €</span>
                  </PreviewReadOnlyValue>
                  <p className="mt-1 text-xs text-app-text-subtle tabular-nums">
                    {incomingPayment.skontoApplied > 0
                      ? `${formatGermanNumber(payableBase)} € − ${formatGermanNumber(incomingPayment.skontoApplied)} € Skonto`
                        + (skontoTaken ? ' (gezogen)' : '')
                      : incomingPayment.skontoExpired
                        ? 'Ohne Skonto, Skontofrist abgelaufen'
                        : `Ohne Skonto (keine Frist) · mit Skonto ${formatGermanNumber(Math.max(0, payableBase - incomingSkontoAmount))} €`}
                  </p>
                </PreviewField>
              ) : null}
              {isIncoming ? (
                <>
                  <PreviewField label="IBAN" tone={fieldTone.payee_iban}>
                    {incomingLocked && row.payee_iban ? (
                      <PreviewLockedValue><span className="font-mono">{formatIbanDisplay(row.payee_iban)}</span></PreviewLockedValue>
                    ) : (
                    <input
                      value={payeeIbanDraft}
                      disabled={saving}
                      onChange={(e) => {
                        setPayeeIbanError(null);
                        setPayeeIbanDraft(e.target.value);
                      }}
                      onBlur={() => {
                        const normalized = normalizeIban(payeeIbanDraft);
                        if (normalized && !isValidIban(normalized)) {
                          setPayeeIbanError('IBAN ist ungültig.');
                          return;
                        }
                        const next = formatIbanDisplay(payeeIbanDraft);
                        setPayeeIbanDraft(next);
                        void commitIfChanged(normalizeIban(row.payee_iban), normalized, {
                          payee_iban: normalized || null,
                        });
                      }}
                      className={`${PREVIEW_INPUT_CLASS} font-mono`}
                      autoComplete="off"
                    />
                    )}
                    {payeeIbanError ? <p className="mt-1 text-xs text-rose-600">{payeeIbanError}</p> : null}
                  </PreviewField>
                  <PreviewField label="BIC">
                    <input
                      value={payeeBicDraft}
                      disabled={saving}
                      onChange={(e) => setPayeeBicDraft(e.target.value)}
                      onBlur={() => {
                        void commitIfChanged(row.payee_bic ?? '', payeeBicDraft, {
                          payee_bic: payeeBicDraft.trim() || null,
                        });
                      }}
                      className={`${PREVIEW_INPUT_CLASS} font-mono uppercase`}
                      autoComplete="off"
                    />
                  </PreviewField>
                  <PreviewField label="Kontoinhaber">
                    <input
                      value={payeeNameDraft}
                      disabled={saving}
                      onChange={(e) => setPayeeNameDraft(e.target.value)}
                      onBlur={() => {
                        void commitIfChanged(row.payee_name ?? '', payeeNameDraft, {
                          payee_name: payeeNameDraft.trim() || null,
                        });
                      }}
                      className={PREVIEW_INPUT_CLASS}
                    />
                  </PreviewField>
                  <PreviewField label="Verwendungszweck">
                    <input
                      value={paymentReferenceDraft}
                      disabled={saving}
                      onChange={(e) => setPaymentReferenceDraft(e.target.value)}
                      onBlur={() => {
                        const next = paymentReferenceDraft.replace(/\s+/g, ' ').trim();
                        setPaymentReferenceDraft(next);
                        void commitIfChanged(row.payment_reference ?? '', next, {
                          payment_reference: next || null,
                        });
                      }}
                      className={PREVIEW_INPUT_CLASS}
                      autoComplete="off"
                    />
                  </PreviewField>
                </>
              ) : null}
              <PreviewField label="Status">
                <AppSelectDropdown
                  value={invoiceStatusSelectValue(row.status, tab)}
                  onChange={(value) => { void onPatch({ status: value }); }}
                  options={statusOptionsForRow(tab, row).map((option) => ({
                    value: option.id,
                    label: statusDisplayLabel(option.id, tab, row),
                  }))}
                  disabled={saving || isInvoiceStatusLockedDuringExtraction(row.status)}
                  triggerClassName={PREVIEW_SELECT_TRIGGER}
                  menuZIndex={dropdownZ}
                />
              </PreviewField>
              {showRejectionReason ? (
                <PreviewField label="Ablehnungsgrund">
                  <AppSelectDropdown
                    value={rejectionReason ?? 'other'}
                    onChange={(value) => { void onPatch({ rejection_reason: value }); }}
                    options={USER_SELECTABLE_REJECTION_REASONS.map((reason) => ({
                      value: reason,
                      label: INVOICE_REJECTION_REASON_LABEL[reason],
                    }))}
                    disabled={saving}
                    triggerClassName={PREVIEW_SELECT_TRIGGER}
                    menuZIndex={dropdownZ}
                  />
                </PreviewField>
              ) : null}
            </div>
          </aside>
        </div>
      </div>
      {createSupplierOpen ? (
        // Drawer liegt per Portal über der Vorschau (normales Modal läge darunter).
        <CreateOrganizationModal
          variant="drawer"
          initialName={unassignedSupplierName}
          initialType="Lieferant"
          onClose={() => setCreateSupplierOpen(false)}
          onCreated={(org) => {
            setCreateSupplierOpen(false);
            onOrganizationCreated?.(org);
            void onPatch({ organization_id: String(org.id), party_name: org.name });
          }}
        />
      ) : null}
    </div>
  );
}
